import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isTrackablePath, shouldTrackAnalytics, trackPageView } from '../utils/siteAnalytics';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_TRACKING_ID = 'G-MQ2VW5Q8EV';

const GoogleAnalytics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Initialize Google Analytics with consent mode
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'functionality_storage': 'denied',
        'personalization_storage': 'denied',
        'ad_storage': 'denied'
      });
      
      // Check if consent has been given
      const consentData = localStorage.getItem('cookieConsent');
      if (consentData) {
        try {
          const consent = JSON.parse(consentData);
          window.gtag('consent', 'update', {
            'analytics_storage': consent.analytics ? 'granted' : 'denied',
            'functionality_storage': consent.functional ? 'granted' : 'denied',
            'personalization_storage': consent.marketing ? 'granted' : 'denied',
            'ad_storage': consent.marketing ? 'granted' : 'denied'
          });
        } catch (e) {
          console.error('Error parsing consent data:', e);
        }
      }
    }
  }, []);

  useEffect(() => {
    const pagePath = location.pathname + location.search;
    if (!isTrackablePath(location.pathname)) return;
    if (!shouldTrackAnalytics()) return;

    trackPageView(pagePath);

    // Track page views when location changes
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', GA_TRACKING_ID, {
        page_path: pagePath,
        page_title: document.title,
        page_location: window.location.href
      });
    }
  }, [location]);

  // Track custom events
  const trackEvent = (action: string, category: string, label?: string, value?: number) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value
      });
    }
  };

  // Track business-specific events
  useEffect(() => {
    if (!isTrackablePath(location.pathname)) return;
    if (!shouldTrackAnalytics()) return;

    // Track scroll depth
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
        maxScroll = scrollPercent;
        trackEvent('scroll', 'engagement', `${scrollPercent}%`);
      }
    };

    // Track outbound links
    const handleLinkClick = (event: Event) => {
      const target = event.target as HTMLAnchorElement;
      if (target.tagName === 'A' && target.href) {
        const isExternal = !target.href.includes(window.location.hostname);
        if (isExternal) {
          trackEvent('click', 'outbound_link', target.href);
        }
      }
    };

    // Track phone number clicks
    const handlePhoneClick = (event: Event) => {
      const target = event.target as HTMLAnchorElement;
      if (target.href && target.href.startsWith('tel:')) {
        trackEvent('click', 'phone_call', target.href.replace('tel:', ''));
      }
    };

    // Track email clicks
    const handleEmailClick = (event: Event) => {
      const target = event.target as HTMLAnchorElement;
      if (target.href && target.href.startsWith('mailto:')) {
        trackEvent('click', 'email', target.href.replace('mailto:', ''));
      }
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleLinkClick);
    document.addEventListener('click', handlePhoneClick);
    document.addEventListener('click', handleEmailClick);

    // Track time on page
    const startTime = Date.now();
    const trackTimeOnPage = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      if (timeSpent > 30) { // Only track if user spent more than 30 seconds
        trackEvent('timing', 'time_on_page', location.pathname, timeSpent);
      }
    };

    // Track when user leaves the page
    window.addEventListener('beforeunload', trackTimeOnPage);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleLinkClick);
      document.removeEventListener('click', handlePhoneClick);
      document.removeEventListener('click', handleEmailClick);
      window.removeEventListener('beforeunload', trackTimeOnPage);
    };
  }, [location]);

  // Expose trackEvent function globally for use in other components
  useEffect(() => {
    (window as any).trackEvent = trackEvent;
  }, []);

  return null;
};

export default GoogleAnalytics;